import React, { useEffect } from 'react';
import { Navigate, useLocation, useParams } from 'react-router-dom';
import { useAuth } from '@/context/auth';
import { useLanguage } from '@/context/language-context';
import { User } from '@/types';

interface RequireAuthProps {
  children: React.ReactNode;
  requireRole?: string;
  requirePermission?: (user: User | null) => boolean;
}

const RequireAuth: React.FC<RequireAuthProps> = ({ children, requireRole, requirePermission }) => {
  const { user, isLoading } = useAuth();
  const location = useLocation();
  const { lang } = useParams<{ lang: string }>();
  const { defaultLanguage } = useLanguage();

  useEffect(() => {
    console.log('RequireAuth: Current auth state:', {
      user: user ? { id: user.id, role: user.role, sub: user.subscriptionTier } : null,
      isLoading,
      requireRole,
      hasCustomPermissionCheck: !!requirePermission,
      path: location.pathname,
      language: lang
    });
  }, [user, isLoading, requireRole, requirePermission, location.pathname, lang]);

  // If authentication is loading, show a loading indicator
  if (isLoading) {
    console.log('RequireAuth: Auth is still loading');
    return (
      <div className="flex justify-center items-center min-h-[80vh]">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  // If the user is not authenticated, redirect to the login page
  if (!user) {
    console.log("RequireAuth: No user found, redirecting to login");
    
    // Always redirect to the login page with the current language prefix
    const loginPath = `/${lang || defaultLanguage}/login`;
    
    return (
      <Navigate 
        to={loginPath} 
        state={{ from: location }} 
        replace 
      />
    );
  }

  // If a specific role is required and user doesn't have it
  if (requireRole && user.role !== requireRole) {
    console.log(`RequireAuth: User doesn't have required role (${requireRole}), redirecting to dashboard`);
    return (
      <Navigate 
        to={`/${lang || defaultLanguage}/dashboard`}
        replace 
      />
    );
  }

  // If a custom permission check is provided and fails, redirect to dashboard (or another appropriate page)
  if (requirePermission && !requirePermission(user)) {
    console.log(`RequireAuth: Custom permission check failed, redirecting to dashboard`);
    return (
      <Navigate 
        to={`/${lang || defaultLanguage}/dashboard`}
        replace 
      />
    );
  }

  // User is authenticated and meets all requirements
  return <>{children}</>;
};

export default RequireAuth;
